import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CalendarDays,
  Combine,
  Trash2,
  ChevronsUpDown,
} from 'lucide-react'
import { ClarifyEntriesIcon } from '@/src/renderer/components/icons/ClarifyEntriesIcon'
import { DormantFilledIcon } from '@/src/renderer/components/icons/DormantIcon'
import { TaskDropdown } from '@/src/renderer/features/taskManagement/components/TaskDropdown'
import { WhenDropdownContent } from '@/src/renderer/features/taskManagement/components/WhenDropdown/WhenDropdown'
import { TodayIcon, TomorrowIcon } from '@/src/renderer/features/taskManagement/components/WhenDropdown'
import type { Task, Project, StartPeriod } from '@/src/renderer/lib/db/models'
import type { StagedAction } from './useClarifyState'
import { formatRelativeAge } from './clarifyFormatters'
import { MoveProjectDropdown } from './MoveProjectDropdown'
import './ClarifyActionPanel.scss'

export interface ClarifyActionPanelProps {
  activeTask: Task | null
  /**
   * If the active card already has a staged action, this carries its kind so
   * the corresponding button (Heute/Morgen/Demnächst/Ruhen/Verschieben/
   * Löschen) can render a selected state.
   */
  activeStagedAction?: StagedAction
  lifeSpace: 'personal' | 'work' | 'home'
  onStage: (action: StagedAction) => void
  /**
   * Notifies the host (ClarifyView) about the "any dropdown is open" state so
   * keyboard shortcuts like H/M/R/L can be suppressed while the user is
   * interacting with a popover.
   */
  onAnyDropdownOpenChange?: (open: boolean) => void
}

/** Small inline kbd-style badge matching the Figma shortcut chip. */
function ShortcutBadge({ label }: { label: string }) {
  return <span className="clarify-action-shortcut">{label}</span>
}

/**
 * Right-hand action panel of the Clarify-Mode (Figma node 3630:3559).
 *
 * Layout:
 *   - Header: folder icon + task title + "vor X Tagen"
 *   - Separator line
 *   - "Wohin damit?" label
 *   - Actions (5 rows + visual dot between Verschieben and Ruhen):
 *       1. [Heute] [Morgen]                          M
 *       2. [Demnächst … ⇅]                            D
 *       3. [Verschieben … ⇅]                          V
 *       ·   (group-separator dot)
 *       4. [Ruhen lassen]                             R
 *       5. [Löschen]                                  L
 */
export function ClarifyActionPanel({
  activeTask,
  activeStagedAction,
  lifeSpace,
  onStage,
  onAnyDropdownOpenChange,
}: ClarifyActionPanelProps) {
  const selectedKind = activeStagedAction?.kind
  const selectedClass = (kind: StagedAction['kind']) =>
    selectedKind === kind ? 'clarify-action-button--selected' : ''
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'en' ? 'en' : 'de'

  const [laterOpen, setLaterOpen] = useState(false)
  const [moveOpen, setMoveOpen] = useState(false)

  const setLater = useCallback(
    (open: boolean) => {
      setLaterOpen(open)
      onAnyDropdownOpenChange?.(open || moveOpen)
    },
    [moveOpen, onAnyDropdownOpenChange],
  )
  const setMove = useCallback(
    (open: boolean) => {
      setMoveOpen(open)
      onAnyDropdownOpenChange?.(open || laterOpen)
    },
    [laterOpen, onAnyDropdownOpenChange],
  )

  if (!activeTask) {
    return (
      <div className="clarify-action-panel clarify-action-panel--empty">
        <div className="clarify-action-panel__empty-text">
          {t('quickNotes.clarify.emptyState')}
        </div>
      </div>
    )
  }

  const handleToday = () => onStage({ kind: 'today' })
  const handleTomorrow = () => onStage({ kind: 'tomorrow' })
  const handlePause = () => onStage({ kind: 'pause' })
  const handleDelete = () => onStage({ kind: 'delete' })

  const handleLaterChange = (value: {
    date: Date
    time: string | null
    period: StartPeriod | null
  }) => {
    onStage({
      kind: 'later',
      date: value.date,
      time: value.time,
      period: value.period,
    })
    setLater(false)
  }

  const handleProjectSelect = (project: Project) => {
    onStage({ kind: 'move', projectId: project.id, projectName: project.name })
    setMove(false)
  }

  return (
    <div className="clarify-action-panel">
      <header className="clarify-action-panel__header">
        <ClarifyEntriesIcon size={16} className="clarify-action-panel__header-icon" />
        <div className="clarify-action-panel__header-text">
          <div className="clarify-action-panel__title">{activeTask.title}</div>
          <div className="clarify-action-panel__age">
            {formatRelativeAge(activeTask.createdAt, locale)}
          </div>
        </div>
      </header>

      <div className="clarify-action-panel__separator" aria-hidden="true" />

      <div className="clarify-action-panel__actions">
        <div className="clarify-action-panel__row clarify-action-panel__row--split">
          <button
            type="button"
            className={`clarify-action-button ${selectedClass('today')}`.trim()}
            onClick={handleToday}
          >
            <span className="clarify-action-button__icon">
              <TodayIcon size={16} />
            </span>
            <span className="clarify-action-button__label">
              {t('quickNotes.clarify.actionToday')}
            </span>
          </button>
          <button
            type="button"
            className={`clarify-action-button ${selectedClass('tomorrow')}`.trim()}
            onClick={handleTomorrow}
          >
            <span className="clarify-action-button__icon">
              <TomorrowIcon size={16} />
            </span>
            <span className="clarify-action-button__label">
              {t('quickNotes.clarify.actionTomorrow')}
            </span>
          </button>
          <ShortcutBadge label="M" />
        </div>

        <div className="clarify-action-panel__row">
          <TaskDropdown
            open={laterOpen}
            onOpenChange={setLater}
            side="bottom"
            align="start"
            sideOffset={4}
            trigger={
              <button
                type="button"
                className={`clarify-action-button clarify-action-button--full clarify-action-button--with-chevron clarify-action-button--later ${selectedClass('later')}`.trim()}
              >
                <CalendarDays size={16} className="clarify-action-button__icon" />
                <span className="clarify-action-button__label">
                  {t('quickNotes.clarify.actionLater')}
                </span>
                <ChevronsUpDown
                  size={16}
                  className="clarify-action-button__chevron"
                />
              </button>
            }
          >
            <WhenDropdownContent
              value={null}
              onChange={handleLaterChange}
              onClose={() => setLater(false)}
              variant="actionbar"
            />
          </TaskDropdown>
          <ShortcutBadge label="D" />
        </div>

        <div className="clarify-action-panel__row">
          <TaskDropdown
            open={moveOpen}
            onOpenChange={setMove}
            side="bottom"
            align="start"
            sideOffset={4}
            trigger={
              <button
                type="button"
                className={`clarify-action-button clarify-action-button--full clarify-action-button--with-chevron clarify-action-button--move ${selectedClass('move')}`.trim()}
              >
                <Combine size={16} className="clarify-action-button__icon" />
                <span className="clarify-action-button__label">
                  {t('quickNotes.clarify.actionMove')}
                </span>
                <ChevronsUpDown
                  size={16}
                  className="clarify-action-button__chevron"
                />
              </button>
            }
          >
            <MoveProjectDropdown
              lifeSpace={lifeSpace}
              onSelect={handleProjectSelect}
            />
          </TaskDropdown>
          <ShortcutBadge label="V" />
        </div>

        <div className="clarify-action-panel__group-separator" aria-hidden="true" />

        <div className="clarify-action-panel__row">
          <button
            type="button"
            className={`clarify-action-button clarify-action-button--full clarify-action-button--pause ${selectedClass('pause')}`.trim()}
            onClick={handlePause}
          >
            <span className="clarify-action-button__icon">
              <DormantFilledIcon size={16} />
            </span>
            <span className="clarify-action-button__label">
              {t('quickNotes.clarify.actionPause')}
            </span>
          </button>
          <ShortcutBadge label="R" />
        </div>

        <div className="clarify-action-panel__row">
          <button
            type="button"
            className={`clarify-action-button clarify-action-button--full clarify-action-button--destructive ${selectedClass('delete')}`.trim()}
            onClick={handleDelete}
          >
            <Trash2 size={16} className="clarify-action-button__icon" />
            <span className="clarify-action-button__label">
              {t('quickNotes.clarify.actionDelete')}
            </span>
          </button>
          <ShortcutBadge label="L" />
        </div>
      </div>
    </div>
  )
}
